import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiRequest } from "../utils/api";
import useDebouncedEffect from "../hooks/useDebouncedEffect";
import "../styles/PurchaseOrderForm.css";
import PurchaseItemsList from "./PurchaseItemsList";
import { usePurchaseItems } from "../context/PurchaseItemsContext";
import { useNavigate, Link } from "react-router-dom";
import { useAlert } from "../components/AlertModal";
import {
  escapeHtml,
  buildMailLayoutHtml,
  buildPurchaseDetailBlocks,
  buildAssessmentPendingMailBody,
  buildAssessmentPendingMailHtml,
  buildDirectConfirmMailBody,
  buildDirectConfirmMailHtml,
  buildPurchaseConfirmedMailBody,
  buildPurchaseConfirmedMailHtml,
  buildPaymentCompletedMailBody,
  buildPaymentCompletedMailHtml,
} from "./utils/purchaseMailTemplates";

const htmlToPlainText = (html) => {
  if (!html) return "";

  return html
    .replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|tr|table|section|article)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/td>/gi, "\t")
    .replace(/<\/th>/gi, "\t")
    .replace(/<li>/gi, "・")
    .replace(/<\/li>/gi, "\n")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\t+/g, " ")
    .trim();
};

const PurchaseOrderForm = ({
  setSupportId,
  selectedStore,
  setSearchTerm,
  setSearchResults,
}) => {
  const {
    purchaseOrderId,
    setPurchaseOrderId,
    updateTotals,
    order,
    setOrder,
    setItems,
    setAndFetchPurchaseOrderId,
  } = usePurchaseItems();

  const [isEditing, setIsEditing] = useState(false);
  const [purchaseOrderData, setPurchaseOrderData] = useState(null);
  const [CustomerData, setCustomerData] = useState(null);

  const [barcodeInput, setBarcodeInput] = useState("");
  const barcodeInputRef = useRef(null);
  const lastProcessedRef = useRef("");

  const [isExistingCustomer, setIsExistingCustomer] = useState(false);

  const { showAlert, showConfirm } = useAlert();

  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingCurrentStatus, setPendingCurrentStatus] = useState("");
  const [mailForm, setMailForm] = useState({
    to: "",
    subject: "",
    body: "",
    htmlBody: "",
  });
  const [mailEditorMode, setMailEditorMode] = useState("form");
  const [mailEditor, setMailEditor] = useState({
    title: "",
    lead: "",
    greeting: "",
    noticeText: "",
    resultIntro: "",
    items: [],
    summary: {
      totalAmount: 0,
      purchaseBonus: 0,
      shippingAdjustment: 0,
    },
    guideText: "",
    cautionText: "",
    footerNote: "",
    accentColor: "#1f4e79",
  });

  const isShopfront =
    !!order.purchase_number &&
    !order.purchase_number.startsWith("K");
  const [isSendingMail, setIsSendingMail] = useState(false);

  const navigate = useNavigate();

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!purchaseOrderId) return;

      try {
        const purchaseData = await apiRequest(
          `inventory/purchase_orders/${purchaseOrderId}/`,
          "GET"
        );
        setPurchaseOrderData(purchaseData);

        if (purchaseData.customer) {
          const customer = await apiRequest(
            `inventory/customers/${purchaseData.customer}/`,
            "GET"
          );
          setCustomerData(customer);
        }
      } catch (error) {
        console.error("印刷用データの取得に失敗:", error);
      }
    };

    fetchData();
  }, [purchaseOrderId]);

  const formatDate = (date) => {
    if (!date) return "";
    const normalized = String(date).trim().replace(/\//g, "-");

    const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return normalized;

    const [, y, m, d] = match;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return "";
    const birth = new Date(birthDate);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    console.log("📝 入力変更:", name, value);

    setOrder((prevOrder) => {
      const newOrder = {
        ...prevOrder,
        [name]: value,
      };
      console.log("🌀 更新後の order 状態:", newOrder);
      return newOrder;
    });

    if (name === "purchase_bonus" || name === "shipping_adjustment") {
      console.log(`💰 ${name} 変更による合計再計算を実行`);
      updateTotals();
    }

    if (purchaseOrderId) {
      try {
        console.log(
          `🌐 API更新: purchase_order (${purchaseOrderId}) に ${name}: ${value}`
        );
        await apiRequest(`inventory/purchase_orders/${purchaseOrderId}/`, "PATCH", {
          [name]: value,
        });
      } catch (error) {
        console.error(`❌ ${name} の更新に失敗（purchase_order）:`, error);
      }
    }

    if (
      order.customer &&
      [
        "customer_name",
        "furigana",
        "phone",
        "email",
        "postal_code",
        "address",
        "birth_date",
      ].includes(name)
    ) {
      const keyMapping = {
        customer_name: "name",
        phone: "phone_number",
      };
      const adjustedKey = keyMapping[name] || name;

      try {
        console.log(`🌐 API更新: 顧客 (${order.customer}) に ${adjustedKey}: ${value}`);
        await apiRequest(`inventory/customers/${order.customer}/`, "PUT", {
          [adjustedKey]: value,
        });
      } catch (error) {
        console.error(`❌ 顧客 ${adjustedKey} の更新に失敗:`, error);
      }
    }
  };

  const checkExistingOrder = async (purchaseNumber) => {
    try {
      const response = await apiRequest(
        `inventory/purchase_orders/?purchase_number=${purchaseNumber}`,
        "GET"
      );

      if (
        response.results &&
        Array.isArray(response.results) &&
        response.results.length > 0
      ) {
        const existingOrder = response.results[0];
        const orderDate =
          existingOrder.date || new Date().toISOString().split("T")[0];

        setOrder((prevOrder) => ({
          ...prevOrder,
          ...existingOrder,
          date: orderDate,
        }));

        return existingOrder;
      }

      return null;
    } catch (error) {
      console.error("エラー発生（GET PurchaseOrder）:", error);
      return null;
    }
  };

  const saveOrder = async () => {
    try {
      if (!order.purchase_number) {
        showAlert("買取番号を入力してください。");
        return;
      }

      let customerId = order.customer;

      const formattedBirthDate = formatDate(order.birth_date);

      const customerData = {
        name: order.customer_name,
        phone_number: order.phone,
        email: order.email,
        postal_code: order.postal_code,
        address: order.address,
        birth_date: formattedBirthDate,
        age: calculateAge(formattedBirthDate),
        furigana: order.furigana,
      };

      if (!isExistingCustomer) {
        // 新規顧客
        try {
          const createdCustomer = await apiRequest(
            "inventory/customers/",
            "POST",
            customerData
          );

          customerId = createdCustomer.id;
          setIsExistingCustomer(true);
        } catch (error) {
          console.error("❌ 顧客登録に失敗:", error);
          showAlert("新規顧客の登録に失敗しました。");
          return;
        }
      } else if (customerId) {
        // 既存顧客
        try {
          await apiRequest(
            `inventory/customers/${customerId}/`,
            "PATCH",
            customerData
          );
        } catch (error) {
          console.error("❌ 顧客情報の更新に失敗:", error);
          showAlert("顧客情報の更新に失敗しました。");
          return;
        }
      }

      setOrder((prevOrder) => ({
        ...prevOrder,
        customer: customerId,
      }));

      const existingOrder = await checkExistingOrder(order.purchase_number);

      const purchaseData = {
        customer: customerId,
        purchase_number: existingOrder
          ? existingOrder.purchase_number
          : order.purchase_number,
        total_items: existingOrder ? existingOrder.total_items : order.total_items,
        purchase_bonus: existingOrder
          ? existingOrder.purchase_bonus
          : order.purchase_bonus,
        shipping_adjustment: existingOrder
          ? existingOrder.shipping_adjustment
          : order.shipping_adjustment,
        total_amount: existingOrder ? existingOrder.total_amount : order.total_amount,
        status: existingOrder ? existingOrder.status : order.status || "未入力あり",
      };

      const endpoint = existingOrder
        ? `inventory/purchase_orders/${existingOrder.id}/`
        : "inventory/purchase_orders/";

      const method = existingOrder ? "PUT" : "POST";

      const response = await apiRequest(endpoint, method, purchaseData);

      const purchaseNumber = purchaseData.purchase_number;
      if (purchaseNumber?.startsWith("K")) {
        await apiRequest("inventory/update_status/", "POST", {
          purchase_number: purchaseNumber,
          status: "assessment_pending",
        });
      }

      setOrder((prevOrder) => ({
        ...prevOrder,
        isOrderLocked: false,
        status: "未入力あり",
        purchase_id: response.id,
        date: response.date,
        total_items: response.total_items,
        purchase_bonus: response.purchase_bonus,
        shipping_adjustment: response.shipping_adjustment,
        total_amount: response.total_amount,
        customer: customerId,
      }));

      setAndFetchPurchaseOrderId(response.id);
      showAlert("伝票が保存されました。");
    } catch (error) {
      console.error("伝票の保存に失敗しました。", error);
      showAlert("伝票の保存に失敗しました。");
    }
  };

  const handlePrint = async (path) => {
    if (!isPrintEnabled) return;

    try {
      // 明細の500ms保存を待つ
      await new Promise((resolve) => setTimeout(resolve, 500));

      // 最新の伝票・明細を取得
      const purchaseData = await apiRequest(
        `inventory/purchase_orders/${purchaseOrderId}/`,
        "GET"
      );

      // stateも最新にしておく
      setPurchaseOrderData(purchaseData);

      navigate(path, {
        state: {
          data: purchaseData,
          customer: CustomerData,
        },
      });
    } catch (error) {
      console.error("印刷データ取得失敗:", error);
      showAlert("最新の伝票データ取得に失敗しました。");
    }
  };

  const isPrizeOrLowPriceKuji = (detail) => {
    const genre1 = detail?.item?.genre1 || "";
    const itemName = detail?.item?.item_name || "";
    const unitPrice = Number(detail?.unit_price || 0);
    const categoryId = Number(detail?.item?.category?.id ?? 0);

    // くじ判定
    const isKuji =
      genre1.includes("くじ") ||
      itemName.includes("くじ");

    // くじは3000円未満だけ合算
    // 3000円以上は個別表示
    if (isKuji) {
      return unitPrice < 3000;
    }

    // くじ以外のプライズ
    const isPrizeByGenre = genre1.includes("プライズ");
    const isPrizeByCategory = [1, 3].includes(categoryId);

    return isPrizeByGenre || isPrizeByCategory;
  };

  const buildEditableDetailTableHtml = (items = []) => {
    if (!items.length) return "";

    const rowsHtml = items
      .map((item) => {
        return `
          <tr>
            <td style="padding:10px 12px; border-bottom:1px solid #eee; vertical-align:top;">
              <div style="font-weight:bold; color:#333;">${escapeHtml(item.name || "")}</div>
              ${
                item.meta
                  ? `<div style="margin-top:4px; font-size:12px; color:#666;">${escapeHtml(item.meta).replace(/\n/g, "<br>")}</div>`
                  : ""
              }
              ${
                item.note
                  ? `<div style="margin-top:4px; font-size:12px; color:#666;">${escapeHtml(item.note).replace(/\n/g, "<br>")}</div>`
                  : ""
              }
            </td>
            <td style="padding:10px 12px; border-bottom:1px solid #eee; text-align:center; white-space:nowrap;">
              ${Number(item.quantity || 0)}点
            </td>
            <td style="padding:10px 12px; border-bottom:1px solid #eee; text-align:right; white-space:nowrap;">
              ￥${Number(item.price || 0).toLocaleString()}
            </td>
          </tr>
        `;
      })
      .join("");

    return `
      <div style="margin:20px 0 16px;">
        <div style="background:#f1f5f9; border-left:4px solid #1f4e79; padding:10px 12px; font-weight:bold; font-size:16px;">
          査定明細
        </div>
        <table style="width:100%; border-collapse:collapse; margin-top:10px; font-size:14px;">
          <thead>
            <tr>
              <th style="text-align:left; padding:10px 12px; background:#fafafa; border-bottom:1px solid #ddd;">商品名 / 状態</th>
              <th style="text-align:center; padding:10px 12px; background:#fafafa; border-bottom:1px solid #ddd; white-space:nowrap;">点数</th>
              <th style="text-align:right; padding:10px 12px; background:#fafafa; border-bottom:1px solid #ddd; white-space:nowrap;">金額</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </div>
    `;
  };

  const buildEditableMailHtml = (editor) => {
    const totalAmount = Number(editor.summary?.totalAmount || 0);
    const purchaseBonus = Number(editor.summary?.purchaseBonus || 0);
    const shippingAdjustment = Number(editor.summary?.shippingAdjustment || 0);

    const detailTableHtml = buildEditableDetailTableHtml(editor.items);

    const itemSubtotal =
      Number(editor.summary?.itemSubtotal) ||
      editor.items.reduce((sum, item) => sum + Number(item.price || 0), 0);

    const contentHtml = `
      ${
        editor.greeting
          ? `<p style="margin:0 0 16px;">${escapeHtml(editor.greeting).replace(/\n/g, "<br>")}</p>`
          : ""
      }

      ${detailTableHtml}

      <div style="margin:20px 0; border:1px solid #e5e5e5; border-radius:8px; overflow:hidden;">
        <table style="width:100%; border-collapse:collapse; font-size:14px;">
          <tr>
            <th style="text-align:left; padding:12px; background:#fafafa;">商品合計</th>
            <td style="text-align:right; padding:12px;">￥${itemSubtotal.toLocaleString()}</td>
          </tr>
          <tr>
            <th style="text-align:left; padding:12px; background:#fafafa;">買取増額</th>
            <td style="text-align:right; padding:12px;">￥${purchaseBonus.toLocaleString()}</td>
          </tr>
          <tr>
            <th style="text-align:left; padding:12px; background:#fafafa;">送料調整</th>
            <td style="text-align:right; padding:12px;">￥${shippingAdjustment.toLocaleString()}</td>
          </tr>
          <tr>
            <th style="text-align:left; padding:12px; background:#f1f5f9;">買取合計</th>
            <td style="text-align:right; padding:12px; font-weight:bold;">￥${totalAmount.toLocaleString()}</td>
          </tr>
        </table>
      </div>

      ${
        editor.guideText
          ? `<p>${escapeHtml(editor.guideText).replace(/\n/g, "<br>")}</p>`
          : ""
      }

      ${
        editor.footerNote
          ? `<p>${escapeHtml(editor.footerNote).replace(/\n/g, "<br>")}</p>`
          : ""
      }
    `;

    return buildMailLayoutHtml({
      title: editor.title || "ご案内",
      lead: editor.lead || "",
      customerName: order.customer_name || "",
      contentHtml,
      accentColor: editor.accentColor || "#1f4e79",
    });
  };

  const buildMailEditorItemsFromDetails = (details = []) => {
    const groupedPrizeItems = [];
    const groupedPrizeDisposalItems = [];
    const normalItems = [];

    let hasPrizeBoxDamage = false;

    const buildMetaText = ({ conditionLabel, boxConditionLabel, accessoryLabel }) => {
      const parts = [];

      if (conditionLabel) {
        if (conditionLabel === "買取不可") {
          parts.push("買取不可");
        } else {
          parts.push(`状態:${conditionLabel}`);
        }
      }

      if (boxConditionLabel) {
        parts.push(`箱:${boxConditionLabel}`);
      }

      if (accessoryLabel) {
        parts.push(`付属品:${accessoryLabel}`);
      }

      return parts.join(" / ");
    };

    for (const detail of details) {
      const quantity = Number(detail.quantity || 0);
      const conditionId = Number(detail?.condition?.id || 0);
      const conditionName = detail?.condition?.name || "";
      const boxCondition = detail?.box_condition || "";
      const accessoryName = detail?.accessories?.name || "";

      const title =
        detail?.item?.item_name ||
        detail?.item?.title ||
        detail?.item_name ||
        "商品名未設定";

      const isGroupedTarget = isPrizeOrLowPriceKuji(detail);
      const isDisposal = conditionId === 7 || conditionName === "買取不可";

      const normalizedItem = {
        id: `${detail?.id ?? "detail"}-${Math.random().toString(36).slice(2, 9)}`,
        detail_id: detail?.id ?? null,
        name: title,
        quantity,
        price: isDisposal ? 0 : Number(detail?.subtotal || 0),
        meta: buildMetaText({
          conditionLabel: conditionName,
          boxConditionLabel: boxCondition,
          accessoryLabel: accessoryName,
        }),
        note: "",
        condition_label: conditionName || null,
        box_condition_label: boxCondition || null,
        accessory_label: accessoryName || null,
      };

      if (isGroupedTarget) {
        if (isDisposal) {
          groupedPrizeDisposalItems.push(normalizedItem);
        } else {
          if (boxCondition === "箱傷み" || boxCondition === "箱破損") {
            hasPrizeBoxDamage = true;
          }
          groupedPrizeItems.push(normalizedItem);
        }
      } else {
        normalItems.push(normalizedItem);
      }
    }

    const result = [...normalItems];

    const groupedPrizeCount = groupedPrizeItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );

    const groupedPrizeAmount = groupedPrizeItems.reduce(
      (sum, item) => sum + Number(item.price || 0),
      0
    );

    const groupedPrizeDisposalCount = groupedPrizeDisposalItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );

    const groupedPrizeDisposalAmount = groupedPrizeDisposalItems.reduce(
      (sum, item) => sum + Number(item.price || 0),
      0
    );

    if (groupedPrizeCount > 0) {
      result.push({
        id: `grouped-prize-${Date.now()}`,
        detail_id: "__GROUPED_PRIZE__",
        name: "プライズ（セットの場合は1点換算）",
        quantity: groupedPrizeCount,
        price: groupedPrizeAmount,
        meta: hasPrizeBoxDamage ? "箱傷みによる減額有り" : "",
        note: "",
        isGrouped: true,
        groupType: "prize",
      });
    }

    if (groupedPrizeDisposalCount > 0) {
      result.push({
        id: `grouped-prize-disposal-${Date.now()}`,
        detail_id: "__GROUPED_PRIZE_DISPOSAL__",
        name: "プライズ買取不可商品",
        quantity: groupedPrizeDisposalCount,
        price: groupedPrizeDisposalAmount,
        meta: "",
        note: "",
        isGrouped: true,
        groupType: "prize_disposal",
      });
    }

    return result;
  };

  const addMailEditorItem = () => {
    setMailEditor((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: "",
          quantity: 1,
          price: 0,
          meta: "",
          note: "",
        },
      ],
    }));
  };

  const updateMailEditorItem = (index, field, value) => {
    setMailEditor((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = {
        ...nextItems[index],
        note: nextItems[index].note ?? "",   // ←これ追加
        [field]:
          field === "quantity" || field === "price"
            ? Number(value || 0)
            : value,
      };
      return {
        ...prev,
        items: nextItems,
      };
    });
  };

  const removeMailEditorItem = (index) => {
    setMailEditor((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateMailEditorField = (field, value) => {
    setMailEditor((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateMailEditorSummary = (field, value) => {
    setMailEditor((prev) => ({
      ...prev,
      summary: {
        ...prev.summary,
        [field]: Number(value || 0),
      },
    }));
  };

  const resetMailEditor = () => {
    setMailEditorMode("form");
    setMailEditor({
      title: "",
      lead: "",
      greeting: "",
      noticeText: "",
      resultIntro: "",
      items: [],
      summary: {
        totalAmount: 0,
        purchaseBonus: 0,
        shippingAdjustment: 0,
      },
      guideText: "",
      cautionText: "",
      footerNote: "",
      accentColor: "#1f4e79",
    });
  };

  const getMailTemplate = (currentStatus, nextStatus) => {
    const purchaseNumber = order.purchase_number || "";
    const customerName = order.customer_name || "お客様";
    const totalAmount = Number(order.total_amount || 0).toLocaleString();

    if (currentStatus === "未入力あり" && nextStatus === "査定完了/承諾待ち") {
      return {
        subject: `【ぱそまる】買取査定結果のお知らせ（買取番号: ${purchaseNumber}）`,
        body: "",
      };
    }

    if (currentStatus === "未入力あり" && nextStatus === "買取確定/振込待ち") {
      return {
        subject: `【ぱそまる】買取査定結果のお知らせ（買取番号: ${purchaseNumber}）`,
        body: "",
      };
    }

    if (
      currentStatus === "査定完了/承諾待ち" &&
      nextStatus === "買取確定/振込待ち"
    ) {
      return {
        subject: `【ぱそまる】買取確定のご案内（買取番号: ${purchaseNumber}）`,
        body: "",
      };
    }

    if (
      currentStatus === "買取確定/振込待ち" &&
      nextStatus === "送金手続き完了"
    ) {
      return {
        subject: `【ぱそまる】送金手続き完了のご案内（買取番号: ${purchaseNumber}）`,
        body: "",
      };
    }

    return {
      subject: `【ぱそまる】ご案内（買取番号: ${purchaseNumber}）`,
      body: `${customerName} 様

いつもお世話になっております。
ぱそまるです。

買取番号 ${purchaseNumber} のステータスが更新されました。
ご確認をお願いいたします。`,
    };
  };
  
  const validatePriceBeforeStatusChange = (details = []) => {
    const invalidItems = details.filter((detail) => {
      const price = Number(detail.unit_price || 0);
      const conditionId = Number(detail?.condition?.id || 0);
      const conditionName = detail?.condition?.name || "";

      const isAllowedZero =
        conditionId === 7 ||
        conditionId === 8 ||
        conditionName === "買取不可" ||
        conditionName === "キャンセル";

      return !isAllowedZero && price <= 0;
    });

    return invalidItems;
  };

  const openStatusMailModal = async (nextStatus) => {
    if (!purchaseOrderId) {
      showAlert("先に伝票を保存してください。");
      return;
    }

    if (!order.email) {
      showAlert("顧客メールアドレスがありません。");
      return;
    }

    try {
      const currentStatus = order.status || "未入力あり";
      const template = getMailTemplate(currentStatus, nextStatus);

      let textBody = template.body || "";
      let htmlBody = "";
      let editorState = {
        title: "ご案内",
        lead: "",
        greeting: "当店をご利用頂き、誠に有難うございます。",
        noticeText: "",
        resultIntro: "",
        items: [],
        summary: {
          totalAmount: Number(order.total_amount || 0),
          purchaseBonus: Number(order.purchase_bonus || 0),
          shippingAdjustment: Number(order.shipping_adjustment || 0),
        },
        guideText: "",
        cautionText: "",
        footerNote: "",
        accentColor: "#1f4e79",
      };

      if (
        currentStatus === "未入力あり" &&
        (nextStatus === "査定完了/承諾待ち" ||
          nextStatus === "買取確定/振込待ち")
      ) {
        const details = await apiRequest(
          `inventory/purchase_orders/${purchaseOrderId}/items/`,
          "GET"
        );

        if (
          currentStatus === "未入力あり" &&
          (nextStatus === "査定完了/承諾待ち" ||
            nextStatus === "買取確定/振込待ち")
        ) {
          const invalidItems = validatePriceBeforeStatusChange(details);

          if (invalidItems.length > 0) {
            showAlert(
              `価格が0円以下の商品があります。\n\n` +
              invalidItems
                .map((d) => {
                  const name =
                    d?.item?.item_name ||
                    d?.item_name ||
                    d?.item?.title ||
                    "商品名未設定";
                  return `・${name}：${Number(d.unit_price || 0).toLocaleString()}円`;
                })
                .join("\n") +
              `\n\n買取不可・キャンセル以外は価格を1円以上にしてください。`
            );
            return;
          }
        }

        const { detailBlock, detailTableHtml } = buildPurchaseDetailBlocks({
          details,
          isPrizeOrLowPriceKuji,
        });

        const commonParams = {
          customerName: order.customer_name || "",
          detailBlock,
          detailTableHtml,
          totalAmount: Number(order.total_amount || 0),
          purchaseBonus: Number(order.purchase_bonus || 0),
          shippingAdjustment: Number(order.shipping_adjustment || 0),
        };

        if (nextStatus === "査定完了/承諾待ち") {
          textBody = buildAssessmentPendingMailBody(commonParams);
          htmlBody = buildAssessmentPendingMailHtml(commonParams);

          editorState = {
            title: "買取金額のお知らせ",
            lead: "査定結果とお手続きのご案内",
            greeting: "当店をご利用頂き、誠に有難うございます。",
            noticeText:
              "本メール送信日より3日以内のお手続きをお願い致します。\n期間を過ぎますと自動承認となりますので、予めご了承下さい。",
            resultIntro: "お送り頂いた商品の、買取査定結果です。",
            items: buildMailEditorItemsFromDetails(details),
            summary: {
              totalAmount: Number(order.total_amount || 0),
              purchaseBonus: Number(order.purchase_bonus || 0),
              shippingAdjustment: Number(order.shipping_adjustment || 0),
            },
            guideText:
              "下記アドレスより承諾・キャンセル等のお手続きをお願いいたします。\nhttps://www.pasomaru.co.jp/member/mypage",
            cautionText:
              "※恐れ入りますが、キャンセルによるご返送料は、お客様負担となっております。",
            footerNote:
              "まとめて査定させていただいている商品は、個別キャンセル等は行えません。\n全て買取か全てキャンセルのどちらかとなります。",
            accentColor: "#1f4e79",
          };
        }

        if (nextStatus === "買取確定/振込待ち") {
          textBody = buildDirectConfirmMailBody(commonParams);
          htmlBody = buildDirectConfirmMailHtml(commonParams);

          editorState = {
            title: "買取金額のお知らせ",
            lead: "査定結果確定のご案内",
            greeting: "当店をご利用頂き、誠に有難うございます。",
            noticeText: "",
            resultIntro: "お送り頂いた商品の、買取査定結果です。",
            items: buildMailEditorItemsFromDetails(details),
            summary: {
              totalAmount: Number(order.total_amount || 0),
              purchaseBonus: Number(order.purchase_bonus || 0),
              shippingAdjustment: Number(order.shipping_adjustment || 0),
            },
            guideText:
              "上記内容にて買取確定となります。振込手続きを進めてまいりますので、今しばらくお待ちください。",
            cautionText: "",
            footerNote: "",
            accentColor: "#1f4e79",
          };
        }
      }

      if (
        currentStatus === "査定完了/承諾待ち" &&
        nextStatus === "買取確定/振込待ち"
      ) {
        const details = await apiRequest(
          `inventory/purchase_orders/${purchaseOrderId}/items/`,
          "GET"
        );

        const safeDetails = Array.isArray(details) ? details : [];

        const itemSubtotal = safeDetails.reduce(
          (sum, d) => sum + Number(d.subtotal || 0),
          0
        );

        const canceledItems = safeDetails
          .filter((d) => Number(d?.condition?.id || 0) === 8)
          .map(
            (d) =>
              d?.item?.item_name ||
              d?.item_name ||
              d?.item?.title ||
              "商品名未設定"
          )
          .filter(Boolean);

        const cancelGuideText =
          canceledItems.length > 0
            ? `${canceledItems
                .map((name) => `${name} をキャンセル致します。`)
                .join("\n")}\n`
            : "";

        textBody = buildPurchaseConfirmedMailBody({
          customerName: order.customer_name || "",
          purchaseNumber: order.purchase_number || "",
          totalAmount: Number(order.total_amount || 0),
        });

        htmlBody = buildPurchaseConfirmedMailHtml({
          customerName: order.customer_name || "",
          purchaseNumber: order.purchase_number || "",
          totalAmount: Number(order.total_amount || 0),
        });

        editorState = {
          title: "買取確定のご案内",
          lead: "ご承諾確認後のお手続きについて",
          greeting: "当店をご利用頂き、誠に有難うございます。",
          items: [],

          summary: {
            totalAmount: Number(order.total_amount || 0),
            purchaseBonus: Number(order.purchase_bonus || 0),
            shippingAdjustment: Number(order.shipping_adjustment || 0),
            itemSubtotal,
          },

          guideText:
            `${cancelGuideText}買取番号 ${order.purchase_number || ""} の買取を確定しました。`,
          footerNote: "振込手続きを進めてまいります。",
          accentColor: "#1f4e79",
        };
      }

      if (
        currentStatus === "買取確定/振込待ち" &&
        nextStatus === "送金手続き完了"
      ) {
        const details = await apiRequest(
          `inventory/purchase_orders/${purchaseOrderId}/items/`,
          "GET"
        );

        const itemSubtotal = (details || []).reduce(
          (sum, d) => sum + Number(d.subtotal || 0),
          0
        );

        textBody = buildPaymentCompletedMailBody({
          customerName: order.customer_name || "",
          purchaseNumber: order.purchase_number || "",
          totalAmount: Number(order.total_amount || 0),
        });

        htmlBody = buildPaymentCompletedMailHtml({
          customerName: order.customer_name || "",
          purchaseNumber: order.purchase_number || "",
          totalAmount: Number(order.total_amount || 0),
        });

        editorState = {
          title: "送金手続き完了のご案内",
          lead: "お振込手続き完了のお知らせ",
          greeting: "当店をご利用頂き、誠に有難うございます。",
          items: [],

          summary: {
            totalAmount: Number(order.total_amount || 0),
            purchaseBonus: Number(order.purchase_bonus || 0),
            shippingAdjustment: Number(order.shipping_adjustment || 0),
            itemSubtotal, 
          },

          guideText: `買取番号 ${order.purchase_number || ""} の送金手続きが完了しました。`,
          footerNote: "ご利用ありがとうございました。",
          accentColor: "#1f4e79",
        };
      }

      setPendingStatus(nextStatus);
      setPendingCurrentStatus(currentStatus);
      setMailEditorMode("form");
      setMailEditor(editorState);
      setMailForm({
        to: order.email || "",
        subject: template.subject,
        body: textBody || "（HTMLメールをご確認ください）",
        htmlBody: htmlBody || buildEditableMailHtml(editorState),
      });
      setMailModalOpen(true);
    } catch (error) {
      console.error("メール本文生成エラー:", error);
      showAlert("メール本文の生成に失敗しました。");
    }
  };

  useEffect(() => {
    if (!mailModalOpen) return;
    if (mailEditorMode !== "form") return;

    const nextHtml = buildEditableMailHtml(mailEditor);

    setMailForm((prev) => ({
      ...prev,
      htmlBody: nextHtml,
      body: htmlToPlainText(nextHtml),
    }));
  }, [mailEditor, mailEditorMode, mailModalOpen]);

  const applyAssessmentBuyPriceUpdates = async () => {
    const data = await apiRequest(
      `inventory/purchase_orders/${purchaseOrderId}/items/`,
      "GET"
    );

    const filteredItems = data.filter(
      (item) =>
        item.condition?.id === 1 &&
        (item.box_condition === null || item.box_condition === "") &&
        item.accessories === null
    );

    for (const item of filteredItems) {
      const storeInventory = item.item?.store_inventory_set?.find(
        (inv) => Number(inv.store) === Number(selectedStore)
      );

      if (!storeInventory) continue;

      const {
        id: storeInventoryId,
        buy_price,
        buy_specialprice_switch,
        version_buy,
      } = storeInventory;

      const unitPrice = Number(item.unit_price);
      let updatedBuyPrice = null;

      if (
        buy_price > 0 &&
        !buy_specialprice_switch &&
        buy_price !== unitPrice
      ) {
        updatedBuyPrice = unitPrice;
      } else if (
        buy_price <= 0 &&
        !buy_specialprice_switch &&
        buy_price !== unitPrice * -1
      ) {
        updatedBuyPrice = unitPrice * -1;
      }

      if (updatedBuyPrice !== null) {
        try {
          await apiRequest(`inventory/storeinventory/${storeInventoryId}/`, "PATCH", {
            buy_price: updatedBuyPrice,
            version_buy: version_buy ?? 1,
          });
          console.log(
            `✅ 更新成功 item_id: ${item.item.id}, buy_price: ${updatedBuyPrice}`
          );
        } catch (error) {
          if (error?.status === 409) {
            showAlert(
              `在庫ID ${storeInventoryId} は他で更新されました。最新情報を取得してください。`
            );
            throw error;
          }
          console.error(`❌ 更新失敗 item_id: ${item.item.id}`, error);
          throw error;
        }
      }
    }
  };

  const sendMailAndUpdateStatus = async () => {
    if (!mailForm.to || !mailForm.subject || !mailForm.htmlBody) {
      showAlert("宛先・件名・HTML本文は必須です。");
      return;
    }

    setIsSendingMail(true);

    try {
      if (
        pendingCurrentStatus === "未入力あり" &&
        pendingStatus === "査定完了/承諾待ち"
      ) {
        await applyAssessmentBuyPriceUpdates();
      }

      const plainBody = htmlToPlainText(mailForm.htmlBody);

      await apiRequest("inventory/mail/send/", "POST", {
        to: mailForm.to,
        subject: mailForm.subject,
        body: plainBody || "HTMLメールをご確認ください。",
        html_body: mailForm.htmlBody,
      });

      let assessmentResult = null;

      const shouldSendAssessmentResult =
        pendingStatus === "査定完了/承諾待ち" ||
        pendingStatus === "買取確定/振込待ち";

      if (shouldSendAssessmentResult) {
        const details = await apiRequest(
          `inventory/purchase_orders/${purchaseOrderId}/items/`,
          "GET"
        );

        const safeDetails = Array.isArray(details) ? details : [];
        const editorItems = Array.isArray(mailEditor.items) ? mailEditor.items : [];
  
        const editorMap = editorItems.reduce((map, item) => {
          if (
            item?.detail_id !== undefined &&
            item?.detail_id !== null &&
            item?.detail_id !== ""
          ) {
            map[`detail:${item.detail_id}`] = item;
          }
          return map;
        }, {});

        const findEditorItem = (detailId, title, quantity, subtotal) => {
          if (
            detailId !== undefined &&
            detailId !== null &&
            detailId !== "" &&
            editorMap[`detail:${detailId}`]
          ) {
            return editorMap[`detail:${detailId}`];
          }

          const exactKey = `${String(title || "").trim()}__${Number(
            quantity || 0
          )}__${Number(subtotal || 0)}`;

          return (
            editorItems.find((item) => {
              const itemKey = `${String(item.name || "").trim()}__${Number(
                item.quantity || 0
              )}__${Number(item.price || 0)}`;
              return itemKey === exactKey;
            }) ||
            editorItems.find(
              (item) => String(item.name || "").trim() === String(title || "").trim()
            )
          );
        };

        const cleanNote = (text) => {
          if (!text) return null;

          const cleaned = String(text)
            // 状態系を削除
            .replace(/(^|\/|\n)\s*状態:[^/\n]+/g, "")
            .replace(/(^|\/|\n)\s*箱:[^/\n]+/g, "")
            .replace(/(^|\/|\n)\s*付属品:[^/\n]+/g, "")

            // 区切り整形
            .replace(/\/+/g, "/")
            .replace(/\n+/g, "\n")

            // 先頭末尾の不要文字削除
            .replace(/^(\s*\/\s*|\s*\n\s*)+/, "")
            .replace(/(\s*\/\s*|\s*\n\s*)+$/, "")

            .trim();

          return cleaned || null;
        };

        const normalItems = [];
        const groupedPrizeItems = [];
        const groupedPrizeDisposalItems = [];

        let hasPrizeBoxDamage = false;

        for (const detail of safeDetails) {
          const quantity = Number(detail?.quantity || 0);
          const subtotal = Number(detail?.subtotal || 0);
          const conditionId = Number(detail?.condition?.id || 0);
          const conditionLabel = detail?.condition?.name || "";
          const boxConditionLabel = detail?.box_condition || "";
          const accessoryLabel = detail?.accessories?.name || "";
          const title =
            detail?.item?.item_name ||
            detail?.item_name ||
            detail?.item?.title ||
            "商品名未設定";

          const isDisposal = conditionId === 7 || conditionLabel === "買取不可";
          const groupedTarget = isPrizeOrLowPriceKuji(detail);

          const editorItem = findEditorItem(
            detail?.id ?? null,
            title,
            quantity,
            isDisposal ? 0 : subtotal
          );

          const normalizedItem = {
            detail_id: detail?.id ?? null,
            title,
            quantity,
            subtotal: isDisposal ? 0 : subtotal,
            condition_id: detail?.condition?.id ?? null,
            condition_label: conditionLabel || null,
            box_condition_label: boxConditionLabel || null,
            accessory_id: detail?.accessories?.id ?? null,
            accessory_label: accessoryLabel || null,
            note: cleanNote(editorItem?.note || editorItem?.meta),
            is_grouped_prize: !!groupedTarget,
          };

          console.log("mailEditor.items", mailEditor.items);

          if (groupedTarget) {
            if (isDisposal) {
              groupedPrizeDisposalItems.push(normalizedItem);
            } else {
              if (
                boxConditionLabel === "箱傷み" ||
                boxConditionLabel === "箱破損"
              ) {
                hasPrizeBoxDamage = true;
              }
              groupedPrizeItems.push(normalizedItem);
            }
          } else {
            normalItems.push(normalizedItem);
          }
        }

        const detailItems = [...normalItems];

        const groupedPrizeCount = groupedPrizeItems.reduce(
          (sum, item) => sum + Number(item.quantity || 0),
          0
        );
        const groupedPrizeAmount = groupedPrizeItems.reduce(
          (sum, item) => sum + Number(item.subtotal || 0),
          0
        );

        const groupedPrizeEditorItem = findEditorItem(
          "__GROUPED_PRIZE__",
          "プライズ（セットの場合は1点換算）",
          groupedPrizeCount,
          groupedPrizeAmount
        );

        if (groupedPrizeCount > 0) {
          detailItems.push({
            detail_id: "__GROUPED_PRIZE__",
            title: "プライズ（セットの場合は1点換算）",
            quantity: groupedPrizeCount,
            subtotal: groupedPrizeAmount,
            condition_id: null,
            condition_label: null,
            box_condition_label: null,
            accessory_id: null,
            accessory_label: null,
            note:
              cleanNote(groupedPrizeEditorItem?.note || groupedPrizeEditorItem?.meta) ||
              (hasPrizeBoxDamage ? "箱傷みによる減額有り" : null),
            is_grouped_prize: true,
          });
        }

        const groupedPrizeDisposalCount = groupedPrizeDisposalItems.reduce(
          (sum, item) => sum + Number(item.quantity || 0),
          0
        );

        const groupedPrizeDisposalEditorItem = findEditorItem(
          "__GROUPED_PRIZE_DISPOSAL__",
          "プライズ買取不可商品",
          groupedPrizeDisposalCount,
          0
        );

        if (groupedPrizeDisposalCount > 0) {
          detailItems.push({
            detail_id: "__GROUPED_PRIZE_DISPOSAL__",
            title: "プライズ買取不可商品",
            quantity: groupedPrizeDisposalCount,
            subtotal: 0,
            condition_id: null,
            condition_label: null,
            box_condition_label: null,
            accessory_id: null,
            accessory_label: null,
            note: cleanNote(
              groupedPrizeDisposalEditorItem?.note ||
                groupedPrizeDisposalEditorItem?.meta
            ),
            is_grouped_prize: true,
          });
        }

        const itemSubtotal =
          normalItems.reduce(
            (sum, item) => sum + Number(item.subtotal || 0),
            0
          ) + groupedPrizeAmount;

        assessmentResult = {
          detail_items: detailItems,
          disposal_count: groupedPrizeDisposalCount,
          disposal_text:
            groupedPrizeDisposalCount > 0
              ? `買取不可品：プライズ買取不可商品 ${groupedPrizeDisposalCount}点`
              : "買取不可品：無し",
          total_amount: Number(itemSubtotal || 0),
          purchase_bonus: Number(mailEditor.summary?.purchaseBonus || 0),
          shipping_adjustment: Math.abs(
            Number(mailEditor.summary?.shippingAdjustment || 0)
          ),
          final_amount: Number(mailEditor.summary?.totalAmount || 0),
        };
      }

      await updateStatus(pendingStatus, assessmentResult);

      setOrder((prev) => ({
        ...prev,
        status: pendingStatus,
        isOrderLocked:
          pendingStatus === "送金手続き完了" || pendingStatus === "キャンセル"
            ? true
            : prev.isOrderLocked,
      }));
      
      if (pendingStatus === "送金手続き完了") {
        const goList = await showConfirm(
          "メール送信とステータス更新が完了しました。",
          {
            okText: "OK",
            cancelText: "一覧へ戻る",
          }
        );

        if (!goList) {
          navigate("/PurchaseOrderSearch");
        }
      } else {
        showAlert("メール送信とステータス更新が完了しました。");
      }
      
      setMailModalOpen(false);
      setPendingStatus("");
      setPendingCurrentStatus("");
      setMailForm({
        to: "",
        subject: "",
        body: "",
        htmlBody: "",
      });
      resetMailEditor();      

    } catch (error) {
      console.error("メール送信またはステータス更新に失敗:", error);
      console.error("error.details:", error?.details);

      showAlert(
        error?.details?.error ||
          error?.message ||
          "メール送信またはステータス更新に失敗しました。"
      );
    } finally {
      setIsSendingMail(false);
    }
  };

  const fetchAndUpdateCustomer = useCallback(
    async (newOrderData, replacePurchaseOrderCustomer = false) => {
      try {
        const searchUrl = `inventory/customers/?email=${encodeURIComponent(
          newOrderData.email
        )}`;

        console.log("🔍 顧客検索URL:", searchUrl);

        const customers = await apiRequest(searchUrl);
        console.log("📡 顧客API応答件数:", customers.length);

        let customerId = null;
        let savedCustomer = null;
        let updatedData = { ...newOrderData };

        const customerData = {
          name: newOrderData.customer_name,
          phone_number: newOrderData.phone,
          email: newOrderData.email,
          postal_code: newOrderData.postal_code,
          address: newOrderData.address,
          birth_date: formatDate(newOrderData.birth_date),
          age: calculateAge(newOrderData.birth_date),
          furigana: newOrderData.furigana,
        };

        // =========================
        // 既存顧客
        // =========================
        if (Array.isArray(customers) && customers.length > 0) {
          const customer = customers[0];

          customerId = customer.id;

          console.log("✅ 既存顧客:", customer);

          // Customer側とQR側を比較
          const fieldsToCheck = [
            {
              orderKey: "customer_name",
              customerKey: "name",
              label: "氏名",
            },
            {
              orderKey: "phone",
              customerKey: "phone_number",
              label: "電話番号",
            },
            {
              orderKey: "email",
              customerKey: "email",
              label: "メール",
            },
            {
              orderKey: "postal_code",
              customerKey: "postal_code",
              label: "郵便番号",
            },
            {
              orderKey: "address",
              customerKey: "address",
              label: "住所",
            },
            {
              orderKey: "birth_date",
              customerKey: "birth_date",
              label: "生年月日",
            },
            {
              orderKey: "furigana",
              customerKey: "furigana",
              label: "フリガナ",
            },
          ];

          for (const field of fieldsToCheck) {
            const currentValue = customer[field.customerKey] || "";
            const qrValue = newOrderData[field.orderKey] || "";

            if (currentValue && qrValue && currentValue !== qrValue) {
              const confirmUpdate = await showConfirm(
                `${field.label}が登録情報と異なります。\n\n` +
                `登録：${currentValue}\n` +
                `QR：${qrValue}\n\n` +
                `QRの情報に更新しますか？`
              );

              if (!confirmUpdate) {
                updatedData[field.orderKey] = currentValue;
              }
            }
          }

          // 確認結果を反映したCustomerデータ
          const finalCustomerData = {
            name: updatedData.customer_name,
            phone_number: updatedData.phone,
            email: updatedData.email,
            postal_code: updatedData.postal_code,
            address: updatedData.address,
            birth_date: formatDate(updatedData.birth_date),
            age: calculateAge(updatedData.birth_date),
            furigana: updatedData.furigana,
          };

          // ★既存伝票のQR差し替え時は、その場でCustomerも更新
          if (replacePurchaseOrderCustomer) {
            savedCustomer = await apiRequest(
              `inventory/customers/${customerId}/`,
              "PATCH",
              finalCustomerData
            );
          } else {
            savedCustomer = customer;
          }
        }

        // =========================
        // 新規顧客
        // =========================
        else {
          console.log("🆕 未登録顧客:", newOrderData.email);

          if (replacePurchaseOrderCustomer) {
            // ★既存伝票へ入れるにはCustomer IDが必要なので即作成
            savedCustomer = await apiRequest(
              "inventory/customers/",
              "POST",
              customerData
            );

            customerId = savedCustomer.id;
          } else {
            // 通常の新規伝票なら今まで通り保存時に作成
            setOrder(newOrderData);
            setIsExistingCustomer(false);
            setBarcodeInput("");
            return;
          }
        }

        updatedData.customer = customerId;

        // =====================================
        // ★既存伝票ならCustomer外部キーを差し替え
        // =====================================
        if (replacePurchaseOrderCustomer && purchaseOrderId) {
          const confirmed = await showConfirm(
            `この伝票の顧客を\n` +
            `「${updatedData.customer_name}」\n` +
            `に変更しますか？`
          );

          if (!confirmed) {
            setBarcodeInput("");
            return;
          }

          await apiRequest(
            `inventory/purchase_orders/${purchaseOrderId}/`,
            "PATCH",
            {
              customer: customerId,
            }
          );

          console.log(
            `✅ 伝票 ${purchaseOrderId} の顧客を ${customerId} に変更`
          );

          // 画面表示更新
          setOrder((prev) => ({
            ...prev,

            // ★伝票番号等は既存値を維持
            purchase_number: prev.purchase_number,

            customer: customerId,
            customer_name: updatedData.customer_name,
            phone: updatedData.phone,
            email: updatedData.email,
            postal_code: updatedData.postal_code,
            address: updatedData.address,
            birth_date: updatedData.birth_date,
            furigana: updatedData.furigana,
          }));

          setCustomerData(savedCustomer);

          setPurchaseOrderData((prev) =>
            prev
              ? {
                  ...prev,
                  customer: customerId,
                }
              : prev
          );

          setIsExistingCustomer(true);
          setBarcodeInput("");

          showAlert(
            `顧客情報を変更しました。\n顧客番号：${customerId}`
          );

          return;
        }

        // 通常の新規伝票
        setOrder(updatedData);
        setIsExistingCustomer(true);
        setBarcodeInput("");

      } catch (error) {
        console.error("❌ 顧客データ取得・変更エラー:", error);
        showAlert("顧客情報の変更に失敗しました。");
      }
    },
    [
      purchaseOrderId,
      setOrder,
      showAlert,
      showConfirm,
    ]
  );

  const handleBarcodeInputChange = (e) => {
    setBarcodeInput(e.target.value);
  };

  const handleDebouncedBarcode = useCallback(() => {
    const rawValue = barcodeInput.trim();

    if (!rawValue || lastProcessedRef.current === rawValue) return;
    lastProcessedRef.current = rawValue;

    const barcodeData = rawValue.split(",");

    if (barcodeData.length >= 8) {
      const normalizedBirthDate = formatDate(barcodeData[6]);

      // 既存伝票を編集しているか
      const isExistingOrderEdit = isEditing && !!purchaseOrderId;

      const newOrderData = {
        ...order,

        // ★編集モードでは現在の買取番号を絶対維持
        purchase_number: isExistingOrderEdit
          ? order.purchase_number
          : barcodeData[0] || order.purchase_number,

        phone: barcodeData[1] || order.phone,
        email: barcodeData[2] || order.email,
        customer_name: barcodeData[3] || order.customer_name,
        postal_code: barcodeData[4] || order.postal_code,
        address: barcodeData[5] || order.address,
        birth_date: normalizedBirthDate || order.birth_date,
        furigana: barcodeData[7] || order.furigana,

        customer: null,
      };

      fetchAndUpdateCustomer(newOrderData, isExistingOrderEdit);
    } else {
      console.warn("⚠️ QRコード形式不正 or 要素不足:", barcodeData);
    }
  }, [
    barcodeInput,
    order,
    isEditing,
    purchaseOrderId,
    fetchAndUpdateCustomer,
  ]);

  useDebouncedEffect(handleDebouncedBarcode, [barcodeInput], 500);

  const initialOrderState = {
    date: new Date().toISOString().split("T")[0],
    purchase_number: "",
    phone: "",
    email: "",
    customer_name: "",
    postal_code: "",
    address: "",
    birth_date: "",
    furigana: "",
    purchase_bonus: 0,
    shipping_adjustment: 0,
    total_amount: 0,
    total_items: 0,
    status: "",
    isOrderLocked: true,
  };

  const resetOrder = () => {
    setOrder(() => {
      const newOrder = { ...initialOrderState };
      return newOrder;
    });

    setPurchaseOrderId(null);
    setItems([]);
    setBarcodeInput("");
    setMailModalOpen(false);
    setPendingStatus("");
    setPendingCurrentStatus("");
    setMailForm({
      to: "",
      subject: "",
      body: "",
      htmlBody: "",
    });
    resetMailEditor();
  };

  const generateDateTimeId = () => {
    const d = new Date();
    const pad = (n) => n.toString().padStart(2, "0");

    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      pad(d.getHours()) +
      pad(d.getMinutes()) +
      pad(d.getSeconds())
    );
  };

  const handleNewOrdershopfront = () => {
    resetOrder();
    navigate("/purchase_orderpage");

    const uniqueId = generateDateTimeId();
    const fixedValue = `${uniqueId},066636382,webadmain2@pasomaru.co.jp,店頭買取,5560005,大阪府大阪市浪速区日本橋4-15-14,1995-09-22,テントウカイトリ,`;

    setBarcodeInput(fixedValue);

    setTimeout(() => {
      if (!barcodeInputRef.current) return;

      barcodeInputRef.current.focus();

      const enterEvent = new KeyboardEvent("keydown", {
        key: "Enter",
        code: "Enter",
        keyCode: 13,
        which: 13,
        bubbles: true,
      });

      barcodeInputRef.current.dispatchEvent(enterEvent);
    }, 0);
  };

  const handleNewOrder = () => {
    resetOrder();
    navigate("/purchase_orderpage");
  };

  const deleteOrder = async () => {
    if (!purchaseOrderId) {
      showAlert("削除する伝票がありません。");
      return;
    }

    const confirmDelete = showConfirm("この伝票を削除しますか？");
    if (!confirmDelete) return;

    try {
      await apiRequest(`inventory/purchase_orders/${purchaseOrderId}/`, "DELETE");

      setOrder({ ...initialOrderState });
      setPurchaseOrderId(null);
      setItems([]);
      handleNewOrder();

      showAlert("伝票が削除されました。");
    } catch (error) {
      console.error("伝票削除エラー:", error);
      showAlert("伝票の削除に失敗しました。");
    }
  };

  const updateStatus = async (newStatus, assessmentResult = null) => {
    try {
      await apiRequest(`inventory/purchase_orders/${purchaseOrderId}/`, "PUT", {
        status: newStatus,
      });

      if (order.purchase_number?.startsWith("K")) {
        const webPayload = {
          purchase_number: order.purchase_number,
          status: newStatus,
        };

        if (assessmentResult) {
          webPayload.assessment_result = assessmentResult;
        }

        await apiRequest("inventory/update_status/", "POST", webPayload);
      }

      setOrder((prevOrder) => ({
        ...prevOrder,
        status: newStatus,
      }));

      updateTotals();
    } catch (error) {
      console.error("Error updating status:", error);
      showAlert("ステータスの更新に失敗しました。");
      throw error;
    }
  };

  const buildAssessmentResultPayload = () => {
    return {
      detail_items: mailEditor.items.map((item) => ({
        title: item.name || "",
        quantity: Number(item.quantity || 0),
        subtotal: Number(item.price || 0),
        note: item.meta || "",
        is_grouped_prize: !!item.isGrouped,
      })),
      total_amount: Number(mailEditor.summary.totalAmount || 0),
      purchase_bonus: Number(mailEditor.summary.purchaseBonus || 0),
      shipping_adjustment: Number(mailEditor.summary.shippingAdjustment || 0),
      final_amount: Number(mailEditor.summary.totalAmount || 0),
    };
  };

  const updatefinal = () => {
    openStatusMailModal("査定完了/承諾待ち");
  };

  const confirmPurchase = () => {
    openStatusMailModal("買取確定/振込待ち");
  };

  const confirmOrder = async () => {
    if (isShopfront) {
      const confirmed = showConfirm("店頭買取を振込済みに変更しますか？");
      if (!confirmed) return;

      try {
        await updateStatus("送金手続き完了");

        setOrder((prev) => ({
          ...prev,
          status: "送金手続き完了",
          isOrderLocked: true,
        }));

        showAlert("店頭買取を振込済みに変更しました。");
      } catch (error) {
        console.error("店頭買取の振込済み更新エラー:", error);
        showAlert("振込済みへの変更に失敗しました。");
      }
      return;
    }

    openStatusMailModal("送金手続き完了");
  };

  const cancelPurchase = async () => {
    const confirmed = showConfirm("この伝票をキャンセルに変更しますか？");
    if (!confirmed) return;

    try {
      await updateStatus("キャンセル");

      setOrder((prev) => ({
        ...prev,
        status: "キャンセル",
        isOrderLocked: true,
      }));

      showAlert("ステータスをキャンセルに変更しました。");
    } catch (error) {
      console.error("キャンセル更新エラー:", error);
      showAlert("キャンセルへの変更に失敗しました。");
    }
  };

  const currentStatus = order.status || "未入力あり";

  const canMoveToAssessment = currentStatus === "未入力あり";
  const canMoveToConfirmed =
    currentStatus === "未入力あり" ||
    currentStatus === "査定完了/承諾待ち";
  const canMoveToCompleted = currentStatus === "買取確定/振込待ち";

  const canMoveToCancelled =
    currentStatus === "未入力あり" ||
    currentStatus === "査定完了/承諾待ち";

  const isActionEnabled = [
    "査定完了/承諾待ち",
    "買取確定/振込待ち",
    "送金手続き完了",
    "キャンセル",
  ].includes(order.status);

  const isPrintEnabled =
    !!purchaseOrderId &&
    !!purchaseOrderData &&
    !!CustomerData;

  return (
    <>
      <div className="purchase-order-container">
        <div className="header">
          <div className="title-bar">
            <h1 className="title">買取伝票</h1>
            <input
              ref={barcodeInputRef}
              type="text"
              value={barcodeInput}
              onChange={handleBarcodeInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            />
          </div>

          <div className="purchase-order-button-group">
            <button className="shopfront-order-btn" onClick={handleNewOrdershopfront}>
              店頭買取
            </button>
            <button className="new-order-btn" onClick={handleNewOrder}>
              新規登録
            </button>
            <button className="delete-btn" onClick={deleteOrder}>
              削除
            </button>
          </div>
        </div>

        <div className="order-content">
          <div className="customer-info">
            <h2 className="section-title">
              顧客情報
              <span className="sub-info">（顧客番号: {order.customer}）</span>
            </h2>

            <div className="info-group">
              <label>氏名（フリガナ）:</label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="customer_name"
                    value={order.customer_name || ""}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    name="furigana"
                    value={order.furigana || ""}
                    onChange={handleChange}
                  />
                </>
              ) : (
                <span>
                  {order.customer_name || ""}({order.furigana || ""})
                </span>
              )}
            </div>

            <div className="info-group">
              <label>電話番号:</label>
              {isEditing ? (
                <input
                  type="text"
                  name="phone"
                  value={order.phone || ""}
                  onChange={handleChange}
                />
              ) : (
                <span>{order.phone || ""}</span>
              )}
            </div>

            <div className="info-group">
              <label>メール:</label>
              {isEditing ? (
                <input
                  type="text"
                  name="email"
                  value={order.email || ""}
                  onChange={handleChange}
                />
              ) : (
                <span>{order.email || ""}</span>
              )}
            </div>

            <div className="info-group">
              <label>住所:</label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="postal_code"
                    value={order.postal_code || ""}
                    onChange={handleChange}
                    placeholder="郵便番号"
                  />
                  <input
                    type="text"
                    name="address"
                    value={order.address || ""}
                    onChange={handleChange}
                    placeholder="住所"
                  />
                </>
              ) : (
                <span>
                  〒{order.postal_code || ""} {order.address || ""}
                </span>
              )}
            </div>

            <div className="info-group">
              <label>生年月日:</label>
              {isEditing ? (
                <input
                  type="date"
                  name="birth_date"
                  value={order.birth_date || ""}
                  onChange={handleChange}
                />
              ) : (
                <span>
                  {order.birth_date || ""} ({calculateAge(order.birth_date) || ""}歳)
                </span>
              )}
            </div>
          </div>

          <div className="order-info">
            <h2 className="section-title">
              伝票情報
              <span className="sub-info">
                （日付: {order.date} / 買取番号: {order.purchase_number}/ 伝票番号:{" "}
                {purchaseOrderId}）
              </span>
            </h2>

            <div className="info-group">
              <label>買取増額:</label>
              {isEditing ? (
                <input
                  type="number"
                  name="purchase_bonus"
                  value={order.purchase_bonus}
                  onChange={handleChange}
                />
              ) : (
                <span>￥{Number(order.purchase_bonus).toLocaleString()}</span>
              )}
            </div>

            <div className="info-group">
              <label>送料調整額:</label>
              {isEditing ? (
                <input
                  type="number"
                  name="shipping_adjustment"
                  value={order.shipping_adjustment}
                  onChange={handleChange}
                />
              ) : (
                <span>￥{Number(order.shipping_adjustment).toLocaleString()}</span>
              )}
            </div>

            <div className="info-group">
              <label>総買取点数:</label>
              <span>{order.total_items}</span>
            </div>

            <div className="info-group">
              <label>買取総計:</label>
              <span>￥{Number(order.total_amount).toLocaleString()}</span>
            </div>

            <div className="info-group">
              <label>ステータス:</label>
              <span>{order.status}</span>
            </div>
          </div>
        </div>

        <div className="buttons-area">
          <div className="action-buttons">
            <div className="normal-actions">
              {!purchaseOrderId && (
                <button className="save-btn" onClick={saveOrder}>
                  伝票保存
                </button>
              )}

              {order.purchase_number && (
                <button className="edit-btn" onClick={toggleEdit}>
                  {isEditing ? "編集完了" : "編集"}
                </button>
              )}

              {order.purchase_number && !isShopfront && canMoveToAssessment && (
                <button className="status-btn complete-assessment" onClick={updatefinal}>
                  査定完了/承諾待ち
                </button>
              )}

              {order.purchase_number && !isShopfront && canMoveToConfirmed && (
                <button className="status-btn confirm-purchase" onClick={confirmPurchase}>
                  買取確定/振込待ち
                </button>
              )}

              {order.purchase_number && (isShopfront || canMoveToCompleted) && (
                <button className="confirm-btn" onClick={confirmOrder}>
                  振込済み
                </button>
              )}
            </div>

            {order.purchase_number && !isShopfront && canMoveToCancelled && (
              <div className="danger-actions">
                <button className="status-btn cancel-purchase" onClick={cancelPurchase}>
                  キャンセル
                </button>
              </div>
            )}
          </div>

          <div className="purchase-order-button-group2">
            <Link
              to="#"
              className={`action-btn print-invoice ${
                !isPrintEnabled ? "disabled" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();

                if (!isPrintEnabled) return;

                handlePrint(`/print-purchase-order-shop/${purchaseOrderId}`);
              }}
            >
              古物台帳
            </Link>

            <Link
              to="#"
              className={`action-btn print-invoice ${
                !isPrintEnabled ? "disabled" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();

                if (!isPrintEnabled) return;

                handlePrint(`/print-purchase-order/${purchaseOrderId}`);
              }}
            >
              伝票印刷
            </Link>
          </div>
        </div>

        <div className="order-items">
          <PurchaseItemsList
            purchaseOrderId={purchaseOrderId}
            isOrderLocked={
              purchaseOrderId &&
              (order.status === "送金手続き完了" || order.status === "キャンセル")
                ? true
                : false
            }
            setSupportId={setSupportId}
            selectedStore={selectedStore}
            setSearchTerm={setSearchTerm}
            setSearchResults={setSearchResults}
          />
        </div>
      </div>

      {mailModalOpen && (
        <div className="purchase-mail-modal-overlay">
          <div className="purchase-mail-modal purchase-mail-modal-wide purchase-mail-modal-xl">
            <h3>メール送信</h3>

            <div className="purchase-mail-field">
              <label>宛先</label>
              <input
                type="text"
                value={mailForm.to}
                onChange={(e) =>
                  setMailForm((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
              />
            </div>

            <div className="purchase-mail-field">
              <label>件名</label>
              <input
                type="text"
                value={mailForm.subject}
                onChange={(e) =>
                  setMailForm((prev) => ({
                    ...prev,
                    subject: e.target.value,
                  }))
                }
              />
            </div>

            <div className="purchase-mail-mode-tabs">
              <button
                type="button"
                className={mailEditorMode === "form" ? "active" : ""}
                onClick={() => setMailEditorMode("form")}
              >
                かんたん編集
              </button>
              <button
                type="button"
                className={mailEditorMode === "html" ? "active" : ""}
                onClick={() => setMailEditorMode("html")}
              >
                HTML直接編集
              </button>
            </div>

            <div className="purchase-mail-editor-grid">
              <div className="purchase-mail-editor-pane purchase-mail-editor-pane-left">
                {mailEditorMode === "form" ? (
                  <>
                    <label className="purchase-mail-pane-title">かんたん編集</label>

                    <div className="purchase-mail-form-section">
                      <div className="purchase-mail-two-col">
                        <div className="purchase-mail-field">
                          <label>タイトル</label>
                          <input
                            type="text"
                            value={mailEditor.title}
                            onChange={(e) => updateMailEditorField("title", e.target.value)}
                          />
                        </div>

                        <div className="purchase-mail-field">
                          <label>サブタイトル</label>
                          <input
                            type="text"
                            value={mailEditor.lead}
                            onChange={(e) => updateMailEditorField("lead", e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="purchase-mail-field">
                        <label>挨拶文</label>
                        <textarea
                          rows={3}
                          value={mailEditor.greeting}
                          onChange={(e) => updateMailEditorField("greeting", e.target.value)}
                        />
                      </div>

                      <div className="purchase-mail-field">
                        <label>注意文</label>
                        <textarea
                          rows={4}
                          value={mailEditor.noticeText}
                          onChange={(e) => updateMailEditorField("noticeText", e.target.value)}
                        />
                      </div>

                      <div className="purchase-mail-field">
                        <label>査定結果の見出し</label>
                        <input
                          type="text"
                          value={mailEditor.resultIntro}
                          onChange={(e) => updateMailEditorField("resultIntro", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="purchase-mail-form-section">
                      <div className="purchase-mail-section-header">
                        <span>査定明細</span>
                        <button type="button" onClick={addMailEditorItem}>
                          ＋ 行を追加
                        </button>
                      </div>

                      <div className="purchase-mail-items-list">
                        {mailEditor.items.map((item, index) => (
                          <div
                            className="purchase-mail-item-card"
                            data-grouped={item.isGrouped ? "true" : "false"}
                            key={item.id || index}
                          >  
                            <div className="purchase-mail-field">
                              <label>商品名</label>
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) =>
                                  updateMailEditorItem(index, "name", e.target.value)
                                }
                              />
                            </div>

                            <div className="purchase-mail-three-col">
                              <div className="purchase-mail-field">
                                <label>点数</label>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    updateMailEditorItem(index, "quantity", e.target.value)
                                  }
                                />
                              </div>

                              <div className="purchase-mail-field">
                                <label>金額</label>
                                <input
                                  type="number"
                                  value={item.price}
                                  onChange={(e) =>
                                    updateMailEditorItem(index, "price", e.target.value)
                                  }
                                />
                              </div>

                              <div className="purchase-mail-field purchase-mail-item-delete-wrap">
                                <label>&nbsp;</label>
                                <button
                                  type="button"
                                  className="purchase-mail-item-delete"
                                  onClick={() => removeMailEditorItem(index)}
                                >
                                  削除
                                </button>
                              </div>
                            </div>

                            <div className="purchase-mail-field">
                              <label>補足</label>
                              <textarea
                                rows={2}
                                value={item.note || ""}
                                onChange={(e) =>
                                  updateMailEditorItem(index, "note", e.target.value)
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="purchase-mail-form-section">
                      <div className="purchase-mail-section-header">
                        <span>金額</span>
                      </div>

                      <div className="purchase-mail-three-col">
                        <div className="purchase-mail-field">
                          <label>合計額</label>
                          <input
                            type="number"
                            value={mailEditor.summary.totalAmount}
                            onChange={(e) =>
                              updateMailEditorSummary("totalAmount", e.target.value)
                            }
                          />
                        </div>

                        <div className="purchase-mail-field">
                          <label>買取増額</label>
                          <input
                            type="number"
                            value={mailEditor.summary.purchaseBonus}
                            onChange={(e) =>
                              updateMailEditorSummary("purchaseBonus", e.target.value)
                            }
                          />
                        </div>

                        <div className="purchase-mail-field">
                          <label>送料負担超過分</label>
                          <input
                            type="number"
                            value={mailEditor.summary.shippingAdjustment}
                            onChange={(e) =>
                              updateMailEditorSummary("shippingAdjustment", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="purchase-mail-form-section">
                      <div className="purchase-mail-field">
                        <label>案内文</label>
                        <textarea
                          rows={4}
                          value={mailEditor.guideText}
                          onChange={(e) => updateMailEditorField("guideText", e.target.value)}
                        />
                      </div>

                      <div className="purchase-mail-field">
                        <label>注意書き</label>
                        <textarea
                          rows={3}
                          value={mailEditor.cautionText}
                          onChange={(e) => updateMailEditorField("cautionText", e.target.value)}
                        />
                      </div>

                      <div className="purchase-mail-field">
                        <label>補足文</label>
                        <textarea
                          rows={3}
                          value={mailEditor.footerNote}
                          onChange={(e) => updateMailEditorField("footerNote", e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="purchase-mail-pane-title">HTML本文</label>
                    <textarea
                      className="purchase-mail-html-textarea"
                      rows={24}
                      value={mailForm.htmlBody}
                      onChange={(e) =>
                        setMailForm((prev) => ({
                          ...prev,
                          htmlBody: e.target.value,
                          body: htmlToPlainText(e.target.value),
                        }))
                      }
                    />
                  </>
                )}
              </div>

              <div className="purchase-mail-editor-pane">
                <label className="purchase-mail-pane-title">プレビュー</label>
                <div className="purchase-mail-preview-shell">
                  <div
                    className="purchase-mail-preview-content"
                    dangerouslySetInnerHTML={{ __html: mailForm.htmlBody }}
                  />
                </div>
              </div>
            </div>

            <div className="purchase-mail-actions">
              <button
                type="button"
                onClick={() => {
                  setMailModalOpen(false);
                  setPendingStatus("");
                  setPendingCurrentStatus("");
                  setMailForm({
                    to: "",
                    subject: "",
                    body: "",
                    htmlBody: "",
                  });
                  resetMailEditor();
                }}
                disabled={isSendingMail}
              >
                キャンセル
              </button>

              <button
                type="button"
                onClick={sendMailAndUpdateStatus}
                disabled={isSendingMail}
              >
                {isSendingMail ? "送信中..." : "送信してステータス更新"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PurchaseOrderForm;